import { Flags } from "@airhelp/react-ui-components";
import type { FlagProps } from "components/Flags";
import config from "config/config";

const LANGUAGES = [
  "bg",
  "cs",
  "da",
  "de",
  "de-at",
  "de-ch",
  "en",
  "en-au",
  "en-ca",
  "en-gb",
  "en-in",
  "en-int",
  "en-ie",
  "en-nz",
  "es",
  "el",
  "fr",
  "fr-be",
  "fr-ca",
  "hu",
  "it",
  "nl",
  "nb",
  "pl",
  "pt-pt",
  "pt-br",
  "ru",
  "ro",
  "ro-md",
  "fi",
  "sv",
  "zh-cn",
  "tr",
  "en-default",
  "fr-default",
  "de-default",
] as const;

export type LanguageCode = typeof LANGUAGES[number];

export const alternateLanguages: Record<"en-default" | "fr-default" | "de-default", LanguageCode[]> = {
  "en-default": ["en", "en-au", "en-ca", "en-gb", "en-in", "en-int", "en-ie", "en-nz"],
  "fr-default": ["fr", "fr-be", "fr-ca"],
  "de-default": ["de", "de-at", "de-ch"],
};

export type Language = {
  name: string;
  hreflang: string;
  hrefsToTranslation: string;
  languageFlag: FlagProps["flag"];
  popularLanguage?: boolean;
  hidden?: boolean;
  localDomain?: string;
};

export default new Map<LanguageCode, Language>(
  Object.entries<Language>({
    bg: {
      name: "Български",
      hreflang: "bg-BG",
      hrefsToTranslation: "bg_bg",
      languageFlag: Flags.Bg,
      hidden: true,
    },
    cs: {
      name: "Čeština",
      hreflang: "cs-CZ",
      hrefsToTranslation: "cs_cz",
      languageFlag: Flags.Cs,
      hidden: true,
    },
    da: {
      name: "Dansk",
      hreflang: "da-DK",
      hrefsToTranslation: "da_dk",
      languageFlag: Flags.Dk,
    },
    de: {
      name: "Deutsch",
      hreflang: "de-DE",
      hrefsToTranslation: "de_de",
      languageFlag: Flags.De,
    },
    "de-at": {
      name: "Deutsch (Österreich)",
      hreflang: "de-AT",
      hrefsToTranslation: "de_de",
      languageFlag: Flags.At,
    },
    "de-ch": {
      name: "Deutsch (Schweiz)",
      hreflang: "de-CH",
      hrefsToTranslation: "de_de",
      languageFlag: Flags.Ch,
    },
    en: {
      name: "English (USA)",
      hreflang: "en-US",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.Us,
      popularLanguage: true,
    },
    "en-au": {
      name: "English (Australia)",
      hreflang: "en-AU",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.Au,
    },
    "en-ca": {
      name: "English (Canada)",
      hreflang: "en-CA",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.Ca,
      localDomain: config.domainEnCa,
    },
    "en-gb": {
      name: "English (UK)",
      hreflang: "en-GB",
      hrefsToTranslation: "en_gb",
      languageFlag: Flags.Gb,
      popularLanguage: true,
    },
    "en-in": {
      name: "English (India)",
      hreflang: "en-IN",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.In,
    },
    "en-int": {
      name: "English (International)",
      hreflang: "x-default",
      hrefsToTranslation: "en_001",
      languageFlag: Flags.Int,
    },
    "en-ie": {
      name: "English (Ireland)",
      hreflang: "en-IE",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.Ie,
    },
    "en-nz": {
      name: "English (New Zealand)",
      hreflang: "en-NZ",
      hrefsToTranslation: "en_us",
      languageFlag: Flags.Nz,
    },
    es: {
      name: "Español",
      hreflang: "es-ES",
      hrefsToTranslation: "es_es",
      languageFlag: Flags.Es,
    },
    el: {
      name: "Eλληνικα",
      hreflang: "el-GR",
      hrefsToTranslation: "el_gr",
      languageFlag: Flags.Gr,
    },
    fr: {
      name: "Français",
      hreflang: "fr-FR",
      hrefsToTranslation: "fr_fr",
      languageFlag: Flags.Fr,
      popularLanguage: true,
    },
    "fr-be": {
      name: "Français (Belgium)",
      hreflang: "fr-BE",
      hrefsToTranslation: "fr_fr",
      languageFlag: Flags.Be,
    },
    "fr-ca": {
      name: "Français (Canada)",
      hreflang: "fr-CA",
      hrefsToTranslation: "fr_fr",
      languageFlag: Flags.Ca,
    },
    hu: {
      name: "Magyar",
      hreflang: "hu-HU",
      hrefsToTranslation: "hu_hu",
      languageFlag: Flags.Hu,
      hidden: true,
    },
    it: {
      name: "Italiano",
      hreflang: "it-IT",
      hrefsToTranslation: "it_it",
      languageFlag: Flags.It,
    },
    nl: {
      name: "Nederlands",
      hreflang: "nl-NL",
      hrefsToTranslation: "nl_nl",
      languageFlag: Flags.Nl,
    },
    nb: {
      name: "Norsk",
      hreflang: "nb-NO",
      hrefsToTranslation: "nb_no",
      languageFlag: Flags.No,
    },
    pl: {
      name: "Polski",
      hreflang: "pl-PL",
      hrefsToTranslation: "pl_pl",
      languageFlag: Flags.Pl,
    },
    "pt-br": {
      name: "Português (Brasil)",
      hreflang: "pt-BR",
      hrefsToTranslation: "pt_br",
      languageFlag: Flags.Br,
    },
    "pt-pt": {
      name: "Português (Portugal)",
      hreflang: "pt-PT",
      hrefsToTranslation: "pt_pt",
      languageFlag: Flags.Pt,
      popularLanguage: true,
    },
    ru: {
      name: "Pусский",
      hreflang: "ru-RU",
      hrefsToTranslation: "ru_ru",
      languageFlag: Flags.Ru,
    },
    ro: {
      name: "Română",
      hreflang: "ro-RO",
      hrefsToTranslation: "ro_ro",
      languageFlag: Flags.Ro,
    },
    "ro-md": {
      name: "молдовеняскэ",
      hreflang: "ro-MD",
      hrefsToTranslation: "ro_md",
      languageFlag: Flags.Md,
      hidden: true,
    },
    fi: {
      name: "Suomi",
      hreflang: "fi-FI",
      hrefsToTranslation: "fi_fi",
      languageFlag: Flags.Fi,
    },
    sv: {
      name: "Svenska",
      hreflang: "sv-SE",
      hrefsToTranslation: "sv_se",
      languageFlag: Flags.Se,
    },
    "zh-cn": {
      name: "汉语",
      hreflang: "zh-CN",
      hrefsToTranslation: "zh_cn",
      languageFlag: Flags.Cn,
    },
    tr: {
      name: "Türkçe",
      hreflang: "tr-TR",
      hrefsToTranslation: "tr_tr",
      languageFlag: Flags.Tr,
    },
  }) as [LanguageCode, Language][]
);
